import {
  DetailIds,
  Emphases,
  LabelIds,
  PaymentPlans,
  Perspectives,
  RowIds,
  SectionIds,
  Stages,
  TooltipIds,
} from "../consts";
import { defineRows } from "../defineRows";
import type { PriceFacts, PriceView } from "../types";
import {
  bookingTotalCaption,
  bookingTotalLabel,
  dayRateDiscountLabel,
  diySpaceRentalLabel,
  diySpaceRentalRateLabel,
  entitlementCaption,
  entitlementLabel,
  foodDrinkMinimumLabel,
  fsvSpaceRentalLabel,
  pendingOfferOffset,
  staffGratuityLabel,
  attendeeCaption,
} from "./labelRules";

/**
 * Guest fee rows (split fee, processing, taxes, credits, total) are hidden from
 * hosts on DIY only. FSV hosts see the full run through "Guest total" — the
 * guest is charged at the venue, so the host needs the guest-facing figures.
 */
//TODO: Review this logic with Karissa
const isGuestAndDIYOnly = (facts: PriceFacts, view: PriceView): boolean =>
  view.perspective !== Perspectives.Host || !facts.isDiy;

/**
 * PreCheckout (inquiry, search, listings) and Checkout show the "rate x hours"
 * space-rental readout (1D); Offer and PostCheckout keep the booking-subtotal
 * copy (1A). 1D also requires a known hourly rate — without one, the label
 * would substitute 0 and read "$0.00 x N hours" next to a real subtotal, so
 * this falls back to 1A instead.
 */
const isPreCheckoutOrCheckoutWithRate = (
  facts: PriceFacts,
  view: PriceView,
): boolean =>
  (view.stage === Stages.PreCheckout || view.stage === Stages.Checkout) &&
  facts.hourlyRate != null;

/**
 * Section 1 — price breakdown. Numbering matches the FSV + DIY Pricing Rules
 * sheet (mobile-aligned). DIY/FSV and dateless/dated differences are mutually
 * exclusive `show` predicates — Rules Sheet variants = separate RowIds.
 */
const priceBreakdownRows = defineRows(SectionIds.PriceBreakdown, {
  charges: [
    // 1A — DIY, dates known, Offer/PostCheckout
    {
      id: RowIds.DiySpaceRental,
      amount: (facts) => facts.spaceRental,
      label: (facts) => diySpaceRentalLabel(facts),
      caption: attendeeCaption(LabelIds.AttendeeRange),
      offset: pendingOfferOffset,
      show: (facts, view) =>
        facts.datesKnown &&
        facts.isDiy &&
        !isPreCheckoutOrCheckoutWithRate(facts, view),
      tooltipId: () => TooltipIds.DiySpaceRental,
    },
    // 1D — DIY, dates known, PreCheckout/Checkout reads the hourly rate x hours
    {
      id: RowIds.DiySpaceRentalRate,
      amount: (facts) => facts.spaceRental,
      label: (facts) => diySpaceRentalRateLabel(facts),
      caption: attendeeCaption(LabelIds.AttendeeRange),
      offset: pendingOfferOffset,
      show: (facts, view) =>
        facts.datesKnown &&
        facts.isDiy &&
        isPreCheckoutOrCheckoutWithRate(facts, view),
      tooltipId: () => TooltipIds.DiySpaceRental,
    },
    // 1B — dateless search shows the hourly rate, not a booking subtotal
    {
      id: RowIds.SpaceRentalReadout,
      amount: (facts) => facts.hourlyRate,
      caption: attendeeCaption(LabelIds.AttendeeRangeReadout),
      show: (facts) => !facts.datesKnown,
    },
    // 1C — FSV with no flat space-rental rate reads "Included"
    {
      id: RowIds.FsvSpaceRental,
      amount: (facts) => facts.spaceRental,
      label: (facts) => fsvSpaceRentalLabel(facts),
      zeroAs: LabelIds.Included,
      offset: pendingOfferOffset,
      show: (facts) => facts.datesKnown && !facts.isDiy,
    },
    // 2A - DIY only - shows when day rate discount is applied
    {
      id: RowIds.DayRateDiscount,
      amount: (facts) => facts.dayRateDiscount,
      label: (facts) => dayRateDiscountLabel(facts),
      negative: true,
      show: (facts) => facts.isDiy && facts.datesKnown,
    },
    // 2B — DIY only - readout shows the percentage itself
    {
      id: RowIds.DayRateDiscountReadout,
      amount: (facts) => facts.dayRateDiscountPercent,
      value: (facts) => ({
        kind: "percent",
        value: facts.dayRateDiscountPercent ?? 0,
      }),
      show: (facts) => facts.isDiy && !facts.datesKnown,
    },
    // 3 — Entitlement - when entitlement is applied
    {
      id: RowIds.Entitlement,
      amount: (facts) => facts.entitlement?.amount,
      show: (facts, view) =>
        facts.entitlement != null && view.perspective !== Perspectives.Host,
      label: (facts) => entitlementLabel(facts),
      caption: (facts) => entitlementCaption(facts),
      negative: true,
    },
    // 4 - FSV only
    {
      id: RowIds.FoodDrinkMinimum,
      amount: (facts) => facts.foodDrinkMinimum,
      label: (facts) => foodDrinkMinimumLabel(facts),
      offset: pendingOfferOffset,
      tooltipId: () => TooltipIds.FoodDrinkMinimum,
    },
    // 5 - When Add-ons are applied
    {
      id: RowIds.AddOnsTotal,
      amount: (facts) => facts.addOnsTotal,
      label: () => ({ id: LabelIds.AddOnsTotal }),
      offset: pendingOfferOffset,
      detail: () => DetailIds.AddOnsTotal,
    },
    // Waiting on Host team implementation of overages/additional payments
    // 6 - Overages - When additional payments are applied
    // {
    //   id: RowIds.AdditionalPaymentsTotal,
    //   amount: (facts) => facts.additionalPaymentsTotal,
    //   label: () => ({ id: LabelIds.AdditionalPaymentsTotal }),
    // },

    // 7 Show cleaning fee on DIY only
    {
      id: RowIds.CleaningFee,
      amount: (facts) => facts.cleaningFee,
      show: (facts) => facts.isDiy,
      label: () => ({ id: LabelIds.CleaningFee }),
      tooltipId: () => TooltipIds.CleaningFee,
    },
    // 8 Show split payment fee on DIY and guest chose split payment
    // Guest only DIY
    {
      id: RowIds.SplitPaymentFee,
      amount: (facts) => facts.splitPaymentFee,
      show: (facts, view) =>
        facts.isDiy && view.paymentPlan === PaymentPlans.SplitPayment,
      perspective: Perspectives.Guest,
      label: () => ({ id: LabelIds.SplitPaymentFee }),
      tooltipId: () => TooltipIds.SplitPaymentFee,
    },
    // Gratuity is currently DEACTIVATED on listings but will keep this in case it's reactivated in the future
    {
      id: RowIds.StaffGratuity,
      amount: (facts) => facts.gratuityAmount,
      label: (facts) => staffGratuityLabel(facts),
      offset: pendingOfferOffset,
      tooltipId: () => TooltipIds.StaffGratuity,
      show: (facts) =>
        facts.gratuityAmount != null && facts.gratuityRate != null,
    },
  ],
  // Divider will appear here
  fees: [
    // 10 — entering fees draws the mid-section divider (9)
    // Processing fee - only show if there's a processing fee value
    {
      id: RowIds.ProcessingFee,
      amount: (facts) => facts.processingFee,
      show: (facts, view) => isGuestAndDIYOnly(facts, view),
      label: () => ({ id: LabelIds.ProcessingFee }),
      tooltipId: () => TooltipIds.ProcessingFee,
      strikethrough: (facts) => facts.originalProcessingFee,
    },
    // 11  - Taxes  - only show if there's not a VAT tax and greater than 0
    {
      id: RowIds.Taxes,
      amount: (facts) => facts.taxes,
      show: (facts, view) => isGuestAndDIYOnly(facts, view),
      label: () => ({ id: LabelIds.Taxes }),
      tooltipId: (facts) =>
        facts.hasVat ? TooltipIds.GuestVatTaxes : undefined,
    },
    // 12 - Credits - only show if there's a credit value and greater than 0
    {
      id: RowIds.Credits,
      amount: (facts) => facts.credits,
      show: (facts, view) => isGuestAndDIYOnly(facts, view),
      label: () => ({ id: LabelIds.Credits }),
      negative: true,
    },
    // 13A / 13B
    {
      id: RowIds.BookingTotal,
      amount: (facts) => facts.displayBookingTotal ?? facts.bookingTotal,
      show: (facts, view) => isGuestAndDIYOnly(facts, view),
      label: (facts, view) => bookingTotalLabel(facts, view),
      caption: (facts, view) => bookingTotalCaption(facts, view),
      offset: pendingOfferOffset,
      emphasis: Emphases.Strong,
      allowZero: true,
    },
  ],
});

export { priceBreakdownRows };
